import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, inject } from '@angular/core';
import { concat, forkJoin, from, Observable, of, Subject } from 'rxjs';
import { catchError, finalize, map, mergeMap, switchMap, takeUntil, tap, toArray } from 'rxjs/operators';
import {
  DrawingCarryAction,
  DrawingCarryDecision,
  DrawingCarryForward,
  DrawingCarryItem,
  DrawingRevisionDetail,
} from '../../../../../../../../shared/models/drawing-sheet';
import { Markup } from '../../../../../../../../shared/models/markup';
import { MarkupService } from '../../../../../../../../shared/services/markup.service';
import { ProjectDrawingsService } from '../../../../../../../../shared/services/project-drawings.service';
import { linkedRecordNoun } from '../drawing-markers';
import { AS_BUILT_STATUS_COPY } from '../drawing-as-built';
import { CommonService } from '../../../../../../../../shared/services/common.service';
import { DrawingsAnalytics } from '../drawings-analytics';

export interface CarryCanvas {
  width: number;
  height: number;
}

/** A nudge in fractions of the page (0–1), so it means the same at any display size. */
export interface CarryNudge {
  x: number;
  y: number;
}

/** The API moves a repositioned copy in canvas pixels of the original redline (`canvas_width` x `canvas_height`). */
export const canvasOffset = (nudge: CarryNudge, canvas: CarryCanvas): { dx: number; dy: number } => ({
  dx: Math.round(nudge.x * canvas.width) || 0,
  dy: Math.round(nudge.y * canvas.height) || 0,
});

/** A drag in screen pixels over the displayed page, as a page fraction. */
export const nudgeFromDrag = (dxPx: number, dyPx: number, display: CarryCanvas): CarryNudge => ({
  x: display.width ? dxPx / display.width : 0,
  y: display.height ? dyPx / display.height : 0,
});

/** Square window around the redline's objects, as CSS for a page image inside a square thumbnail; null shows the whole page. */
export const cropStyle = (data: unknown, canvas: CarryCanvas | null): { width: number; left: number; top: number } | null => {
  const objects = (Array.isArray(data) ? data : (data as { objects?: unknown[] } | null)?.objects) as Record<string, unknown>[] | undefined;
  if (!canvas?.width || !canvas?.height || !Array.isArray(objects) || !objects.length) {
    return null;
  }
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  for (const o of objects) {
    const w = Number(o.width ?? 0) * Number(o.scaleX ?? 1);
    const h = Number(o.height ?? 0) * Number(o.scaleY ?? 1);
    if (typeof o.left !== 'number' || typeof o.top !== 'number') {
      continue;
    }
    const left = o.originX === 'center' ? o.left - w / 2 : o.originX === 'right' ? o.left - w : o.left;
    const top = o.originY === 'center' ? o.top - h / 2 : o.originY === 'bottom' ? o.top - h : o.top;
    x0 = Math.min(x0, left);
    y0 = Math.min(y0, top);
    x1 = Math.max(x1, left + w);
    y1 = Math.max(y1, top + h);
  }
  if (!isFinite(x0)) {
    return null;
  }
  const size = Math.min(Math.max(x1 - x0, y1 - y0, canvas.width * 0.08) * 1.3, Math.max(canvas.width, canvas.height));
  const cx = (x0 + x1) / 2;
  const cy = (y0 + y1) / 2;
  return { width: (canvas.width / size) * 100, left: (-(cx - size / 2) / size) * 100, top: (-(cy - size / 2) / size) * 100 };
};

const ARROW_STEP = 0.002;
const ARROW_STEP_LARGE = 0.02;

export interface CarrySection {
  sheetUuid: string;
  number: string;
  title: string;
  offer: DrawingCarryForward;
  from: DrawingRevisionDetail;
  to: DrawingRevisionDetail;
}

/**
 * Shared redlines on a superseded revision, offered for the sheet's current revision: keep (copy as is), reposition
 * (copy shifted) or retire. Copies are untagged shared rows; links and evidence stay on the old revision.
 */
@Component({
  selector: 'app-drawing-carry-forward',
  templateUrl: './drawing-carry-forward.component.html',
  styleUrls: ['./drawing-carry-forward.component.scss'],
  standalone: false,
})
export class DrawingCarryForwardComponent implements OnChanges, OnDestroy {
  /** The app's toast (Figma 11893:79195): CommonService.notification, no action button */
  private readonly notify = inject(CommonService);
  /** W48: PostHog events */
  private readonly track = inject(DrawingsAnalytics);
  @Input() projectId: string;
  /** Review every sheet a published set touched */
  @Input() setUuid: string | null = null;
  /** Or one sheet */
  @Input() sheetUuid: string | null = null;
  @Input() canPublish = false;
  @Output() back = new EventEmitter<void>();
  @Output() openRevision = new EventEmitter<string>();

  sections: CarrySection[] = [];
  loading = false;
  applying = false;
  readonly decisions = new Map<string, DrawingCarryAction>();
  readonly nudges = new Map<string, CarryNudge>();
  /** The row whose reposition nudge is open */
  nudging: { section: CarrySection; item: DrawingCarryItem } | null = null;
  readonly actions: { value: DrawingCarryAction; label: string }[] = [
    { value: 'keep', label: 'Keep' },
    { value: 'reposition', label: 'Reposition' },
    { value: 'retire', label: 'Retire' },
  ];
  private readonly rows = new Map<string, Markup>();
  private drag: { x: number; y: number; start: CarryNudge; display: CarryCanvas } | null = null;
  private readonly reset = new Subject<void>();
  private readonly onDestroy = new Subject<void>();

  constructor(private api: ProjectDrawingsService, private markup: MarkupService) {}

  get decidedCount(): number {
    return this.decisions.size;
  }

  get canApply(): boolean {
    return this.canPublish && !this.applying && !this.loading && this.decidedCount > 0;
  }

  get offeredCount(): number {
    return this.sections.reduce((n, s) => n + s.offer.items.length, 0);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.projectId && !changes.setUuid && !changes.sheetUuid) {
      return;
    }
    this.reset.next();
    this.sections = [];
    this.decisions.clear();
    this.nudges.clear();
    this.rows.clear();
    this.nudging = null;
    const sheets$: Observable<string[]> = this.setUuid
      ? this.api.getSet(this.projectId, this.setUuid).pipe(map(set => (set.revisions ?? []).map(r => r.match?.sheetUuid).filter(Boolean)))
      : of(this.sheetUuid ? [this.sheetUuid] : []);
    this.loading = true;
    sheets$
      .pipe(
        switchMap(uuids => from(Array.from(new Set(uuids)).map((uuid, index) => ({ uuid, index })))),
        mergeMap(({ uuid, index }) => this.loadSection(uuid).pipe(map(section => ({ section, index }))), 4),
        toArray(),
        finalize(() => (this.loading = false)),
        takeUntil(this.reset),
        takeUntil(this.onDestroy),
      )
      .subscribe({
        next: found => {
          this.sections = found.sort((a, b) => a.index - b.index).map(f => f.section).filter(Boolean);
        },
        error: e => this.fail(e),
      });
  }

  ngOnDestroy(): void {
    this.reset.complete();
    this.onDestroy.next();
    this.onDestroy.complete();
  }

  canvasOf(markupUuid: string): CarryCanvas | null {
    const row = this.rows.get(markupUuid);
    return row?.canvas_width && row?.canvas_height ? { width: row.canvas_width, height: row.canvas_height } : null;
  }

  crop(markupUuid: string): { width: number; left: number; top: number } | null {
    return cropStyle(this.rows.get(markupUuid)?.annotation_data, this.canvasOf(markupUuid));
  }

  /** As-built marks say so, with the status they keep when carried (spec §15 rule 6); the rest are field notes */
  typeLabel(item: DrawingCarryItem): string {
    if (item.kind === 'as_built') {
      return `As-built · ${AS_BUILT_STATUS_COPY[item.review_status ?? 'open'] ?? 'Open'}`;
    }
    return item.kind === 'sketch' ? 'Record mark' : 'Field note';
  }

  /** The API falls back to the entity type when the link has no label */
  linkLabel(item: DrawingCarryItem): string {
    const label = item.link?.label;
    if (!label) {
      return '—';
    }
    const noun = linkedRecordNoun(label);
    return noun === 'Record' ? label : noun;
  }

  decide(markupUuid: string, action: DrawingCarryAction | null): void {
    if (!action) {
      this.decisions.delete(markupUuid);
    } else {
      this.decisions.set(markupUuid, action);
    }
    if (action === 'reposition') {
      const found = this.find(markupUuid);
      this.nudging = found;
    } else if (this.nudging?.item.markup_uuid === markupUuid) {
      this.nudging = null;
    }
  }

  nudgeOf(markupUuid: string): CarryNudge {
    return this.nudges.get(markupUuid) ?? { x: 0, y: 0 };
  }

  offsetText(markupUuid: string): string {
    const canvas = this.canvasOf(markupUuid);
    if (!canvas) {
      return '';
    }
    const { dx, dy } = canvasOffset(this.nudgeOf(markupUuid), canvas);
    return `${dx}, ${dy} px`;
  }

  openNudge(section: CarrySection, item: DrawingCarryItem): void {
    this.decide(item.markup_uuid, 'reposition');
    this.nudging = { section, item };
  }

  closeNudge(): void {
    this.nudging = null;
  }

  resetNudge(markupUuid: string): void {
    this.nudges.delete(markupUuid);
  }

  /** Arrow keys move the ghost 0.2% of the page; with Shift, 2%. */
  onNudgeKey(event: KeyboardEvent, markupUuid: string): void {
    const steps: Record<string, [number, number]> = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
    const step = steps[event.key];
    if (!step) {
      return;
    }
    event.preventDefault();
    const size = event.shiftKey ? ARROW_STEP_LARGE : ARROW_STEP;
    const n = this.nudgeOf(markupUuid);
    this.nudges.set(markupUuid, { x: n.x + step[0] * size, y: n.y + step[1] * size });
  }

  onNudgePointerDown(event: PointerEvent, markupUuid: string): void {
    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    this.drag = { x: event.clientX, y: event.clientY, start: this.nudgeOf(markupUuid), display: { width: rect.width, height: rect.height } };
    target.setPointerCapture?.(event.pointerId);
    target.focus();
  }

  onNudgePointerMove(event: PointerEvent, markupUuid: string): void {
    if (!this.drag) {
      return;
    }
    const moved = nudgeFromDrag(event.clientX - this.drag.x, event.clientY - this.drag.y, this.drag.display);
    this.nudges.set(markupUuid, { x: this.drag.start.x + moved.x, y: this.drag.start.y + moved.y });
  }

  onNudgePointerUp(): void {
    this.drag = null;
  }

  /** Posts the decided rows of each sheet, one sheet at a time; each answer replaces that sheet's open offers. */
  apply(): void {
    if (!this.canApply) {
      return;
    }
    const batches = this.sections
      .map(section => ({ section, items: this.decisionsFor(section) }))
      .filter(b => b.items.length);
    if (!batches.length) {
      return;
    }
    this.applying = true;
    let kept = 0;
    let retired = 0;
    let pending = 0;
    concat(
      ...batches.map(b =>
        this.api.applyCarryForward(this.projectId, b.section.sheetUuid, b.items).pipe(
          tap(offer => {
            b.section.offer = offer;
            for (const item of b.items) {
              this.decisions.delete(item.markup_uuid);
              this.nudges.delete(item.markup_uuid);
              if (item.action === 'retire') {
                retired++;
              } else {
                kept++;
              }
            }
            pending += offer.preview_pending?.length ?? 0;
          }),
        ),
      ),
    )
      .pipe(
        finalize(() => {
          this.applying = false;
          this.sections = this.sections.filter(s => s.offer.items.length);
          if (this.nudging && !this.decisions.has(this.nudging.item.markup_uuid)) {
            this.nudging = null;
          }
        }),
        takeUntil(this.onDestroy),
      )
      .subscribe({
        complete: () => {
          this.track.track('carry_forward_used', { set_uuid: this.setUuid, count: kept + retired });
          const done = [kept ? `${kept} carried forward` : '', retired ? `${retired} retired` : ''].filter(Boolean).join(', ');
          const note = pending ? ' Some previews are still rendering.' : '';
          this.notify.notification(`Redlines: ${done}.${note}`, 'success');
        },
        error: e => this.fail(e),
      });
  }

  trackSection(_: number, s: CarrySection): string {
    return s.sheetUuid;
  }

  private decisionsFor(section: CarrySection): DrawingCarryDecision[] {
    const out: DrawingCarryDecision[] = [];
    for (const item of section.offer.items) {
      const action = this.decisions.get(item.markup_uuid);
      if (!action) {
        continue;
      }
      if (action === 'reposition') {
        const canvas = this.canvasOf(item.markup_uuid);
        if (!canvas) {
          continue;
        }
        out.push({ markup_uuid: item.markup_uuid, action, offset: canvasOffset(this.nudgeOf(item.markup_uuid), canvas) });
      } else {
        out.push({ markup_uuid: item.markup_uuid, action });
      }
    }
    return out;
  }

  private find(markupUuid: string): { section: CarrySection; item: DrawingCarryItem } | null {
    for (const section of this.sections) {
      const item = section.offer.items.find(i => i.markup_uuid === markupUuid);
      if (item) {
        return { section, item };
      }
    }
    return null;
  }

  /** One sheet's offers with both page images and the originals' canvas sizes; null when nothing waits. */
  private loadSection(sheetUuid: string): Observable<CarrySection | null> {
    return this.api.carryForward(this.projectId, sheetUuid).pipe(
      switchMap(offer => {
        if (!offer?.from || !offer.to || !offer.items?.length) {
          return of(null);
        }
        return forkJoin([this.api.getRevision(this.projectId, offer.from.revision_uuid), this.api.getRevision(this.projectId, offer.to.revision_uuid)]).pipe(
          switchMap(([fromRev, toRev]) =>
            this.markup.getByAttachment(fromRev.file.attachment_uuid, fromRev.page_number).pipe(
              catchError(() => of([] as Markup[])),
              map((rows: Markup[]) => {
                for (const row of rows) {
                  this.rows.set(row.id, row);
                }
                return {
                  sheetUuid,
                  number: toRev.sheet?.number ?? '',
                  title: toRev.sheet?.title ?? '',
                  offer,
                  from: fromRev,
                  to: toRev,
                } as CarrySection;
              }),
            ),
          ),
        );
      }),
    );
  }

  private fail(e: { error?: { code?: string } } | null): void {
    this.track.error('carry_forward', e);
    this.notify.notification(this.api.failureMessage(e?.error?.code, this.api.errorMessage(e)), 'error');
  }
}
